import { Controller } from "@hotwired/stimulus"

// Combobox języka treści CMS: wpisujesz, żeby filtrować, ale wybierasz TYLKO poprawny kod
// z listy (bez wolnego wpisu - chroni przed literówkami/śmieciami i psuciem hreflang).
// Nazwy języków są LOKALIZOWANE do języka panelu przez Intl.DisplayNames (Polak widzi
// "Angielski", Francuz "Anglais"), a szukać można po zlokalizowanej nazwie, angielskiej i kodzie.
export default class extends Controller {
  static targets = ["value", "search", "list", "option", "more"]

  connect() {
    // domyślnie pokazujemy tylko języki podstawowe; resztę świata po kliknięciu "+ więcej języków"
    this.expanded = false
    this.localizeOptions()
    this.syncLabelFromValue()
    this.onDocClick = (e) => { if (!this.element.contains(e.target)) this.close() }
    document.addEventListener("click", this.onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
  }

  // Lokalizuje etykiety języków do języka panelu i buduje indeks wyszukiwania
  localizeOptions() {
    const uiLocale = (document.documentElement.lang || "en").trim() || "en"
    let dn = null
    try { dn = new Intl.DisplayNames([uiLocale], { type: "language" }) } catch (_) { dn = null }

    this.optionTargets.forEach((li) => {
      const code = li.dataset.value
      if (!code) return // pusta opcja "brak jezyka" - nie lokalizujemy
      const english = li.dataset.label // np. "English (en)"
      let localized = null
      try { localized = dn ? dn.of(code) : null } catch (_) { localized = null }

      let display = english
      if (localized && localized.toLowerCase() !== code.toLowerCase()) {
        const name = localized.charAt(0).toUpperCase() + localized.slice(1)
        display = `${name} (${code})`
      }
      li.textContent = display
      // szukaj po tym, co widać (zlokalizowana nazwa + kod) - angielskiej etykiety NIE dokładamy,
      // bo jej podłańcuchy dawały fałszywe trafienia niewidoczne w nazwie (np. "pl" w "Simplified" -> chiński)
      li.dataset.search = `${display} ${code}`.toLowerCase()
    })
  }

  open() {
    this.listTarget.hidden = false
    this.searchTarget.setAttribute("aria-expanded", "true")
    this.filter()
  }

  close() {
    this.listTarget.hidden = true
    this.searchTarget.setAttribute("aria-expanded", "false")
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    const searching = q !== ""
    let anyVisible = false
    this.optionTargets.forEach((li) => {
      const isExtra = li.dataset.group === "extra"
      const matches = q === "" || (li.dataset.search || "").includes(q)
      // bez szukania: primary zawsze, extra tylko po rozwinięciu; przy szukaniu: wszystko co pasuje
      const visible = matches && (searching || this.expanded || !isExtra)
      li.hidden = !visible
      if (visible) anyVisible = true
    })
    this.element.querySelectorAll("[data-group-header]").forEach((h) => {
      const group = h.dataset.groupHeader
      const visible = this.optionTargets.some((li) => !li.hidden && li.dataset.group === group)
      h.hidden = !visible
    })
    // przełącznik "+ więcej języków" tylko gdy nie szukamy i lista nie jest rozwinięta
    if (this.hasMoreTarget) this.moreTarget.hidden = searching || this.expanded
    this.listTarget.hidden = !anyVisible && searching
  }

  // Rozwija listę o pozostałe języki świata (grupa extra)
  showMore(event) {
    event.preventDefault()
    this.expanded = true
    this.filter()
  }

  pick(event) {
    this.pickOption(event.currentTarget)
    this.close()
  }

  keydown(event) {
    if (event.key === "Escape") { this.close(); return }
    if (event.key === "Enter") {
      const first = this.optionTargets.find((li) => !li.hidden)
      if (first) { event.preventDefault(); this.pickOption(first); this.close() }
    }
  }

  // Ustawia wartosc z opcji; pusta opcja "brak jezyka" (code="") czysci input do placeholdera
  pickOption(li) {
    const code = li.dataset.value
    this.setValue(code, code === "" ? "" : li.textContent.trim())
  }

  setValue(code, label) {
    this.valueTarget.value = code
    this.searchTarget.value = label
    this.highlightSelected(code)
  }

  // Przenosi podswietlenie na aktualnie wybrana opcje (live po picku, nie tylko z serwera)
  highlightSelected(code) {
    this.optionTargets.forEach((li) => {
      const on = code !== "" && li.dataset.value === code
      li.classList.toggle("bg-blue-50", on)
      li.classList.toggle("font-medium", on)
    })
  }

  syncLabelFromValue() {
    const code = this.valueTarget.value
    if (!code) return
    const li = this.optionTargets.find((o) => o.dataset.value === code)
    if (li) this.searchTarget.value = li.textContent.trim()
  }
}
